/**
 * PortalActionItemPage — D-Q8 action-item launchpad.
 *
 * Dispatches by action_type:
 *   markup   → DocumentAnnotator in comment mode
 *   approve  → DocumentAnnotator in approve mode
 *   sign     → PortalSignModeViewer (read-only, R4 signs via email link)
 *   acknowledge → simple Accept button (no review)
 *   upload | reply | form → ActionTypeComingSoon
 *
 * Gated: both document_review + document_review_portal flags required.
 * If either is off, falls back to legacy acknowledge (Accept button only).
 */
import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DocumentAnnotator } from '@/components/document-review/DocumentAnnotator';
import { ActionTypeComingSoon } from '@/components/portal/ActionTypeComingSoon';
import { PortalSignModeViewer } from '@/components/portal/PortalSignModeViewer';
import { PortalActionChrome } from '@/components/portal/PortalActionChrome';
import {
  IdentityCapture,
  getStoredActorId,
  storeActorId,
  type IdentityCaptureResult,
} from '@/components/portal/IdentityCapture';

const REVIEW_BEARING_TYPES = new Set(['markup', 'approve', 'sign']);
const COMING_SOON_TYPES = new Set(['upload', 'reply', 'form']);

interface ActionItem {
  id: string;
  title: string;
  description?: string | null;
  action_type?: string;
  document_review_id?: string;
  document_review_file_ref?: string;
  document_review_status?: string;
  accepted_at?: string | null;
  /** Hydrated by GET /api/ext/{token}/actions/{id} when available */
  project_name?: string | null;
  due_date?: string | null;
}

export function PortalActionItemPage() {
  const { token: viewToken, actionId } = useParams<{ token: string; actionId: string }>();
  const [searchParams] = useSearchParams();

  const apiBase = (import.meta.env.VITE_API_URL as string) ?? '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<ActionItem | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [actorId, setActorId] = useState<string | null>(null);
  const [showIdentityCapture, setShowIdentityCapture] = useState(false);
  const [pendingLaunch, setPendingLaunch] = useState(false);
  const [viewerMode, setViewerMode] = useState<'comment' | 'approve' | 'sign' | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [inCanvasStampsEnabled, setInCanvasStampsEnabled] = useState<boolean>(true);

  const password = searchParams.get('p') ?? undefined;

  // Restore cached actor id
  useEffect(() => {
    if (viewToken) {
      const cached = getStoredActorId(viewToken);
      if (cached) setActorId(cached);
    }
  }, [viewToken]);

  // Load the action item metadata
  useEffect(() => {
    if (!viewToken || !actionId) return;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const headers: Record<string, string> = {};
        if (password) headers['X-View-Password'] = password;

        const res = await fetch(`${apiBase}/api/ext/${viewToken}/actions/${actionId}`, { headers });
        if (!res.ok) {
          setError('Unable to load this action item.');
          return;
        }
        const data = await res.json();
        setAction(data.action ?? data);
        if (data.action?.accepted_at) setAccepted(true);
        const dr = data.tenant_settings?.document_review;
        if (dr && typeof dr.in_canvas_stamps_enabled === 'boolean') {
          setInCanvasStampsEnabled(dr.in_canvas_stamps_enabled);
        }
      } catch {
        setError('Network error. Please refresh.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [viewToken, actionId, apiBase, password]);

  // Fetch the review PDF as a Blob URL once viewer mode is active.
  // The portal cannot pass raw gs:// file_refs to pdf.js — it must stream bytes
  // through the token-gated /api/portal/reviews/{id}/file endpoint.
  useEffect(() => {
    const reviewId = action?.document_review_id;
    if (!viewerMode || !reviewId || !viewToken || !actorId) return;

    let cancelled = false;
    let createdUrl: string | null = null;

    async function fetchPdf() {
      try {
        const headers: Record<string, string> = { 'X-Actor-Session': actorId! };
        if (password) headers['X-View-Password'] = password;
        const res = await fetch(
          `${apiBase}/api/portal/reviews/${reviewId}/file?view_token=${encodeURIComponent(viewToken!)}`,
          { headers }
        );
        if (!res.ok) throw new Error(`PDF fetch failed: ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setPdfUrl(createdUrl);
      } catch (e) {
        if (!cancelled) {
          setPdfUrl('');
        }
      }
    }
    fetchPdf();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [viewerMode, action?.document_review_id, viewToken, actorId, apiBase, password]);

  function handleActionClick() {
    if (!action) return;
    const atype = action.action_type ?? 'acknowledge';

    if (REVIEW_BEARING_TYPES.has(atype)) {
      if (!actorId) {
        setPendingLaunch(true);
        setShowIdentityCapture(true);
      } else {
        launchReviewMode(atype);
      }
    }
    // acknowledge and coming-soon are handled inline — no explicit click handler needed
  }

  function launchReviewMode(atype: string) {
    if (atype === 'markup') setViewerMode('comment');
    else if (atype === 'approve') setViewerMode('approve');
    else if (atype === 'sign') setViewerMode('sign');
  }

  function handleIdentityCaptured(result: IdentityCaptureResult) {
    storeActorId(viewToken!, result);
    setActorId(result.actorId);
    setShowIdentityCapture(false);
    if (pendingLaunch && action) {
      setPendingLaunch(false);
      launchReviewMode(action.action_type ?? '');
    }
  }

  function handleIdentityCancel() {
    setShowIdentityCapture(false);
    setPendingLaunch(false);
  }

  async function handleAcknowledge() {
    if (!viewToken || !actionId || accepting || accepted) return;
    setAccepting(true);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (password) headers['X-View-Password'] = password;
      await fetch(`${apiBase}/api/ext/${viewToken}/actions/${actionId}/accept`, {
        method: 'POST',
        headers,
      });
      setAccepted(true);
    } finally {
      setAccepting(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="animate-spin w-6 h-6 text-muted-foreground" />
      </div>
    );
  }

  if (error || !action) {
    return <div className="p-6 text-sm text-destructive">{error ?? 'Action not found.'}</div>;
  }

  const atype = action.action_type ?? 'acknowledge';
  const reviewId = action.document_review_id;

  // Active viewer modes
  // The viewer-canvas branches (comment/approve/sign) historically rendered
  // bare — no Banks chrome around them. F12/F13/F14/F17/F20/F30/F31 in the
  // external-stamp demo surfaced this: the customer never saw the firm
  // branding once they entered a review. Wrap each viewer in
  // PortalActionChrome so the sticky branded header + Powered-by footer
  // anchor the viewer the same way they anchor the action card.
  if (viewerMode && reviewId) {
    if (viewerMode === 'sign') {
      return (
        <PortalActionChrome token={viewToken!} apiBase={apiBase} projectName={action.project_name}>
          <PortalSignModeViewer
            viewToken={viewToken!}
            reviewId={reviewId}
            actorId={actorId!}
            pdfUrl={pdfUrl}
            reviewStatus={action.document_review_status}
            apiBase={apiBase}
            onClose={() => setViewerMode(null)}
          />
        </PortalActionChrome>
      );
    }

    if (!pdfUrl) {
      return (
        <PortalActionChrome token={viewToken!} apiBase={apiBase} projectName={action.project_name}>
          <div className="flex items-center justify-center h-48 gap-2 text-muted-foreground">
            <Loader2 className="animate-spin w-5 h-5" />
            <span className="text-sm">Loading document…</span>
          </div>
        </PortalActionChrome>
      );
    }

    return (
      <PortalActionChrome token={viewToken!} apiBase={apiBase} projectName={action.project_name}>
        <DocumentAnnotator
          documentReviewId={reviewId}
          pdfUrl={pdfUrl}
          mode={viewerMode}
          reviewStatus={action.document_review_status}
          onClose={() => setViewerMode(null)}
          isPortal
          portalContext={{
            viewToken: viewToken!,
            actorId: actorId!,
            password,
            inCanvasStampsEnabled,
          }}
        />
      </PortalActionChrome>
    );
  }

  // Coming-soon stub
  if (COMING_SOON_TYPES.has(atype)) {
    return (
      <PortalActionChrome token={viewToken!} apiBase={apiBase} projectName={action.project_name}>
        <ActionTypeComingSoon />
      </PortalActionChrome>
    );
  }

  // Acknowledge (or flag-off fallback for review-bearing types without flags)
  const showAcknowledge = atype === 'acknowledge' || !REVIEW_BEARING_TYPES.has(atype);

  return (
    <PortalActionChrome token={viewToken!} apiBase={apiBase} projectName={action.project_name}>
      <div className="rounded-xl border shadow-sm p-6 space-y-4 max-w-2xl mx-auto bg-background"
        style={{ borderColor: 'var(--border, #e5e7eb)' }}
      >
        {/* Project context header — gives the customer somewhere to anchor before
         *  the call-to-action. Falls back gracefully when project_name isn't
         *  hydrated (older API response shape). */}
        {action.project_name && (
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {action.project_name}
          </p>
        )}

        <h1 className="text-xl font-semibold">{action.title}</h1>

        {/* Action description / instructions */}
        {action.description && (
          <div className="rounded-md border bg-muted/30 p-4 text-sm whitespace-pre-wrap">
            {action.description}
          </div>
        )}

        {/* Optional document context — when the action has a linked review,
         *  surface a "View document" affordance so the customer knows what
         *  they're acknowledging. Only shown for non-review-bearing actions
         *  (review-bearing already open the doc via the primary button). */}
        {!REVIEW_BEARING_TYPES.has(atype) && action.document_review_id && (
          <p className="text-xs text-muted-foreground">
            A document is attached to this action.
          </p>
        )}

        {action.due_date && (
          <p className="text-xs text-muted-foreground">
            Due: {new Date(action.due_date).toLocaleDateString()}
          </p>
        )}

        {showIdentityCapture && viewToken && (
          <IdentityCapture
            viewToken={viewToken}
            onCapture={handleIdentityCaptured}
            onCancel={handleIdentityCancel}
            apiBase={apiBase}
          />
        )}

        <div className="pt-2">
          {REVIEW_BEARING_TYPES.has(atype) && (
            <Button onClick={handleActionClick} disabled={!!viewerMode}>
              {atype === 'markup' && 'Open for review'}
              {atype === 'approve' && 'Review & approve'}
              {atype === 'sign' && 'View signing status'}
            </Button>
          )}

          {showAcknowledge && (
            <Button
              onClick={handleAcknowledge}
              disabled={accepted || accepting}
              variant={accepted ? 'secondary' : 'default'}
            >
              {accepted ? 'Accepted' : accepting ? 'Saving…' : 'Accept'}
            </Button>
          )}
        </div>

        {accepted && (
          <p className="text-xs text-muted-foreground">
            Thank you — your acknowledgement has been recorded.
          </p>
        )}
      </div>
    </PortalActionChrome>
  );
}
